import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import bg from "@/assets/images/bg-reward.webp";
import box from "@/assets/images/box.png";
import logo from "@/assets/images/logo.png";
import rotate from "@/assets/images/rotate.svg";
import { CONSTANTS } from "@/constants";
import { useWalletAddress } from "@/hooks/useWalletAddress";
import { useVerifyStore } from "@/stores/use-verify";
import { useBalance } from "../balance/data";
import { useChestNumber } from "../chest-number/data";
import { useFreeChest } from "../countdown/data";
import { useNeedVerify } from "../open-chest/use-need-verify";
import { BannerReward } from "./banner";
import { useReward } from "./data";
import { EventFreeSmash } from "./event-free-smash";
import { waitForTransactionReceipt } from "@/utils/wait-transaction";

export function Reward(props: { txid: string; onClose: () => void }) {
  const { txid, onClose } = props;
  const [isShowReward, setIsShowReward] = useState<boolean>(false);
  const [endVideo, setPlayEndVideo] = useState<boolean>(false);
  const { t } = useTranslation();
  const address = useWalletAddress();
  const [txhash, setTxhash] = useState<string>();

  const { data } = useReward(address, txhash);

  const { title, img } = useMemo(() => {
    const receive = CONSTANTS.RECEIVE_SMASH.find(
      (item) => item.address?.toLowerCase() === data?.eggAddress.toLowerCase()
    );

    if (!receive) {
      return {
        title: null,
        img: "",
      };
    }

    return {
      title: receive.title,
      img: receive.img,
    };
  }, [data?.eggAddress.toLowerCase]);

  const { mutate: mutateBalance } = useBalance();
  const { mutate: mutateChest } = useChestNumber(address);
  const { mutate: mutateFreeChest } = useFreeChest();
  const { mutate: mutateVerify } = useNeedVerify(address);
  const setVerify = useVerifyStore(useShallow((state) => state.setVerify));
  const handleGetTxHash = useCallback(async () => {
    try {
      const txhash = await waitForTransactionReceipt(txid);
      setTxhash(txhash);
      mutateBalance();
      mutateChest();
      mutateFreeChest();
      mutateVerify();
    } catch (error) {
      // toaster.error("simulation_failed");
      // navigate(`/${CONSTANTS.BASE_PATH}`, { replace: true });
    }
  }, [txid]);

  useEffect(() => {
    setTimeout(() => {
      setIsShowReward(true);
    }, 15_000);
  }, []);

  useEffect(() => {
    handleGetTxHash();
  }, []);

  const handleClose = useCallback(() => {
    mutateBalance();
    mutateChest();
    mutateFreeChest();
    mutateVerify();
    setVerify({});
    onClose();
  }, [mutateBalance, mutateChest, mutateFreeChest, mutateVerify, setVerify]);

  if (!isShowReward && !title) {
    return (
      <div className="w-screen h-screen  flex items-center justify-center fixed z-[99] inset-0">
        <div className="relative w-screen h-screen">
          <img
            src={bg}
            className="w-screen h-screen object-cover"
            alt="reward"
          />
          {endVideo ? (
            <svg
              aria-hidden="true"
              className={clsx(
                "absolute w-8 h-8 text-gray-200 animate-spin fill-[#F68703] z-30",
                {}
              )}
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          ) : null}

          <video
            playsInline
            autoPlay
            muted
            src={`${CONSTANTS.DOMAIN}/open-chest.webm`}
            className="w-screen h-screen object-cover absolute inset-0 z-20"
            poster={bg}
            onEnded={() => {
              setPlayEndVideo(true);
              if (data?.eggAddress.length > 0) {
                setIsShowReward(true);
              }
            }}
          ></video>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center p-4 gap-1 fixed z-[99] inset-0">
      <div
        onClick={() => {
          handleClose();
        }}
        className="flex items-center justify-center relative p-4 mx-4 w-full h-[296px] bg-gradient-to-b from-[#fec537] via-[#f9d519] to-[#f68703] rounded-3xl"
      >
        <div className="absolute inset-0 w-full h-full overflow-hidden opacity-30">
          <img
            src={rotate}
            alt="rotate"
            width={343}
            height={296}
            loading="lazy"
            className="w-full h-full scale-150"
          />
        </div>

        <div className="relative flex-1">
          <div className="flex items-center justify-between">
            <img
              src={logo}
              alt="logo"
              width={117}
              height={64}
              className="w-[58px]"
              loading="lazy"
            />

            <div className="flex items-center gap-1">
              <img src={box} alt="box" width={32} height={24} loading="lazy" />

              <p className="text-[16px] leading-5 font-bold text-black">
                {t("egg_craking")}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <img
              src={img}
              alt="eggs"
              width={552}
              height={450}
              loading="lazy"
              className="w-[184px] h-[150px] object-contain"
            />
          </div>

          <p className="text-[24px] leading-8 font-semibold tracking-[-0.48px] text-center text-black">
            {t(title || "")}
          </p>
        </div>
      </div>
      <BannerReward />
      <EventFreeSmash />
      <div className="flex items-center justify-center mt-4">
        <button
          onClick={() => {
            handleClose();
          }}
          type="button"
        >
          <img
            src={require("@/assets/events/close.webp")}
            className="w-8 h-8"
            alt="close"
          />
        </button>
      </div>
    </div>
  );
}
